import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  styled,
  Slide,
  Fade,
  lighten
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  InfoOutlined,
  WarningAmberOutlined,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * Toast types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Props for the Toast component
 */
export interface ToastProps {
  open: boolean;
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
  showIcon?: boolean;
}

/**
 * Icon mapping for different toast types
 */
const iconMap = {
  success: CheckCircleOutline,
  error: ErrorOutline,
  warning: WarningAmberOutlined,
  info: InfoOutlined,
};

/**
 * Get color for toast type from theme
 */
const getToastColor = (theme: any, type: ToastType) => {
  const colorMap = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  };
  return colorMap[type];
};

/**
 * Get background color for toast type from theme
 */
const getToastBackground = (theme: any, type: ToastType) => {
  // Same subtle tint as before (the type's light shade), but SOLID — not see-through. The old
  // value was `${X.light}20` (the colour at ~12% alpha over white). Lightening X.light ~87% toward
  // white (1 − 0x20/255 ≈ 0.875) reproduces that exact pale colour as an opaque fill.
  const TINT_TOWARD_WHITE = 0.875;
  const backgroundMap = {
    success: lighten(theme.palette.success.light, TINT_TOWARD_WHITE),
    error: lighten(theme.palette.error.light, TINT_TOWARD_WHITE),
    warning: lighten(theme.palette.warning.light, TINT_TOWARD_WHITE),
    info: lighten(theme.palette.info.light, TINT_TOWARD_WHITE),
  };
  return backgroundMap[type];
};

/**
 * Styled toast container
 */
const ToastContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'toastType',
})<{ toastType: ToastType }>(({ theme, toastType }) => ({
  position: 'fixed',
  bottom: '24px',
  left: '24px',
  minWidth: '320px',
  maxWidth: '480px',
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : getToastBackground(theme, toastType),
  border: `1px solid ${getToastColor(theme, toastType)}`,
  borderRadius: '4px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0px 4px 12px rgba(0, 0, 0, 0.5)'
    : '0px 4px 12px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  gap: '12px',
  zIndex: 9999,
  fontFamily: 'Open Sans',
}));

/**
 * Toast component - A replacement for alert dialogs with slide-in animation
 * 
 * This component provides a non-blocking toast notification that slides in
 * from the bottom-left corner and auto-dismisses after a specified duration.
 * 
 * @param props - Component props
 * @returns Toast component
 */
const Toast: React.FC<ToastProps> = ({
  open,
  message,
  type = 'info',
  onClose,
  duration = 4000,
  showIcon = true,
}) => {
  const [visible, setVisible] = useState(false);
  const IconComponent = iconMap[type];

  useEffect(() => {
    if (open) {
      setVisible(true);
      
      // Auto-dismiss after duration
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [open, duration]);

  const handleClose = () => {
    setVisible(false);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!open && !visible) {
    return null;
  }

  return (
    <Slide
      direction="right"
      in={visible}
      timeout={300}
      mountOnEnter
      unmountOnExit
    >
      <ToastContainer toastType={type}>
        {showIcon && (
          <IconComponent 
            sx={{ 
              color: theme => getToastColor(theme, type),
              fontSize: '20px',
              flexShrink: 0
            }} 
          />
        )}
        
        <Typography
          variant="body2"
          sx={{
            color: theme => theme.palette.text.primary,
            fontSize: '14px',
            lineHeight: '1.4',
            fontFamily: 'Open Sans',
            flex: 1,
            margin: 0,
          }}
        >
          {message}
        </Typography>
      </ToastContainer>
    </Slide>
  );
};

export default Toast;
